import { CLIENT_PLATFORM } from "../api/clientPlatform";

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

const GA_ID = process.env.REACT_APP_GA_ID || "";

/**
 * Validate GA Measurement ID format.
 */
function isValidGAId(id: string): boolean {
  return /^G-[A-Z0-9]+$/i.test(id);
}

const GA_ENABLED = isValidGAId(GA_ID);

/**
 * DebugView + console logging — on for local/dev builds, off in production.
 * No env toggle; `npm start` always sends `debug_mode` so events show in GA4 DebugView.
 */
const GA_DEBUG = process.env.NODE_ENV !== "production";

/**
 * Whether GA debug logging / DebugView mode is enabled.
 *
 * @returns True outside production builds.
 */
export function isGADebugMode(): boolean {
  return GA_DEBUG;
}

/** gtag script / dataLayer ready (no measurement config yet). */
let initialized = false;

/**
 * True only after a logged-in admin id is bound via `gtag('config', …, { user_id })`.
 * Until then page_view / custom events / enhanced-measurement session are not started,
 * so anonymous login-page hits do not inflate Active users.
 */
let identityReady = false;

/** Last bound admin id string, or null when logged out. */
let boundUserId: string | null = null;

/**
 * If `setGAUserId` runs before `initGA`, stash the id and apply after init.
 */
let pendingUserId: string | null | undefined = undefined;

/**
 * Ensure dataLayer exists and push a gtag-style call so GA processes it when the script loads.
 * Used when gtag is not yet available (async script still loading).
 */
function pushToDataLayer(...args: any[]) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(args);
  } catch (error) {
    console.debug("[GA] pushToDataLayer failed", error);
  }
}

/**
 * Build gtag config for a logged-in admin. Always includes `user_id`.
 *
 * @param userId - Numeric admin id as string.
 * @returns Config object for `gtag('config', …)`.
 */
function measurementConfig(userId: string): Record<string, unknown> {
  const config: Record<string, unknown> = {
    send_page_view: false, // SPA — we handle page_view after identity bind
    anonymize_ip: true,
    user_id: userId,
  };
  if (GA_DEBUG) {
    config.debug_mode = true;
  }
  return config;
}

/**
 * Push config + user properties for the given admin id.
 *
 * @param userId - Numeric admin id as string.
 */
function bindMeasurement(userId: string) {
  const config = measurementConfig(userId);
  if (typeof window.gtag === "function") {
    window.gtag("set", { user_id: userId });
    window.gtag("set", "user_properties", { admin_id: userId });
    window.gtag("config", GA_ID, config);
  } else {
    pushToDataLayer("set", { user_id: userId });
    pushToDataLayer("set", "user_properties", { admin_id: userId });
    pushToDataLayer("config", GA_ID, config);
  }
}

/**
 * Clear bound identity without calling `config` (avoids starting a new anonymous session).
 */
function clearMeasurementIdentity() {
  if (typeof window.gtag === "function") {
    window.gtag("set", { user_id: null });
    window.gtag("set", "user_properties", { admin_id: null });
  } else {
    pushToDataLayer("set", { user_id: null });
    pushToDataLayer("set", "user_properties", { admin_id: null });
  }
}

/**
 * Prepare gtag / dataLayer only. Does **not** call `gtag('config')` —
 * measurement starts in `setGAUserId` after login so Active users stay tied to admin id.
 */
export function initGA() {
  try {
    if (!GA_ENABLED) {
      console.info("[GA] Disabled — invalid or missing GA ID");
      return;
    }

    if (initialized) return;

    if (typeof window.gtag === "function") {
      window.gtag("js", new Date());
    } else {
      pushToDataLayer("js", new Date());
    }

    initialized = true;
    console.info(
      GA_DEBUG
        ? "[GA] Ready (debug_mode ON; waiting for login user_id)"
        : "[GA] Ready (waiting for login user_id)",
    );

    if (pendingUserId !== undefined) {
      const queued = pendingUserId;
      pendingUserId = undefined;
      setGAUserId(queued);
    }
  } catch (error) {
    console.debug("[GA] initGA failed", error);
  }
}

/**
 * Whether events may be sent (logged-in admin bound).
 *
 * @returns True after `setGAUserId` with a non-null admin id.
 */
export function isGAIdentityReady(): boolean {
  return identityReady;
}

function sendEvent(name: string, params: Record<string, any> = {}) {
  try {
    // Every event (page_view + custom) says which client sent it: web / ios / android.
    const withPlatform = { ...params, client_platform: CLIENT_PLATFORM };
    const payload = GA_DEBUG
      ? { ...withPlatform, debug_mode: true }
      : withPlatform;
    if (GA_DEBUG) {
      console.log("[GA] event →", name, payload);
    }
    if (typeof window.gtag === "function") {
      window.gtag("event", name, payload);
    } else {
      pushToDataLayer("event", name, payload);
    }
  } catch (error) {
    console.debug("[GA] sendEvent failed", error);
  }
}

/**
 * Track page view — only after login identity is bound.
 *
 * @param path - Path (+ search) to record.
 */
export function trackPageView(path: string) {
  try {
    if (!GA_ENABLED || !initialized || !identityReady) {
      if (GA_DEBUG) {
        console.log("[GA] skipped page_view (waiting for login user_id)", {
          page_path: path,
          initialized,
          identityReady,
        });
      }
      return;
    }
    sendEvent("page_view", {
      page_path: path,
      user_id: boundUserId,
    });
  } catch (error) {
    console.debug("[GA] trackPageView failed", error);
  }
}

/**
 * Track custom event — only after login identity is bound.
 *
 * @param name - GA4 event name.
 * @param params - Event params.
 */
export function trackEvent(name: string, params: Record<string, any> = {}) {
  try {
    if (!GA_ENABLED || !initialized || !identityReady) {
      if (GA_DEBUG) {
        console.log("[GA] skipped event (waiting for login user_id)", {
          name,
          params,
          initialized,
          identityReady,
        });
      }
      return;
    }
    sendEvent(name, {
      ...params,
      user_id: boundUserId ?? params.user_id,
    });
  } catch (error) {
    console.debug("[GA] trackEvent failed", error);
  }
}

/**
 * Bind or clear GA identity for logged-in admins.
 * First non-null call runs `gtag('config')` with `user_id` (starts measurement).
 * Sets reserved `user_id` + user property `admin_id`. Never sends email.
 * Failures never interrupt auth or UI flows.
 *
 * @param userId - Numeric admin id, or null to clear on logout.
 */
export function setGAUserId(userId: number | string | null) {
  try {
    if (!GA_ENABLED) return;

    const value =
      userId == null || userId === ""
        ? null
        : String(userId).trim() || null;

    if (!initialized) {
      pendingUserId = value;
      if (GA_DEBUG) {
        console.log("[GA] setGAUserId queued until initGA", { value });
      }
      return;
    }

    if (GA_DEBUG) {
      console.log("[GA] setGAUserId", { userId, value, admin_id: value });
    }

    if (!value) {
      identityReady = false;
      boundUserId = null;
      clearMeasurementIdentity();
      return;
    }

    if (identityReady && boundUserId === value) {
      return;
    }

    bindMeasurement(value);
    boundUserId = value;
    identityReady = true;

    // Land the first authenticated page_view with user_id already set.
    const path =
      typeof window !== "undefined"
        ? `${window.location.pathname}${window.location.search}`
        : "/";
    trackPageView(path);
  } catch (error) {
    console.debug("[GA] setGAUserId failed", error);
  }
}
